"use client";
import clsx from "clsx";

import Button from "@/components/ui/Button";

import styles from "./index.module.css";

import Close from "@/components/icons/Close";
import Modal from "@/components/ui/Modal";
import useModal from "@/hooks/useModal";
import { FormattedFilterGroup } from "@/lib/depict/filters";
import { useUrlSearchParams } from "@frend-digital/ui/next";
import { memo, useMemo, useOptimistic, useTransition } from "react";

export const searchParamOptions = {
  replace: true,
  scroll: false,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FilterBody = ({
  filter,
  startTransition,
  title,
}: {
  filter: FormattedFilterGroup;
  startTransition: React.TransitionStartFunction;
  title: string;
}) => {
  const searchParams = useUrlSearchParams(searchParamOptions);
  const [_value, setOptimisticValue] = useOptimistic(
    searchParams.getAll(filter.key),
  );

  const handleValueChange = (values: string[]) => {
    const value = values[0];
    const current = searchParams.getAll(filter.key);

    startTransition(() => {
      if (current.includes(value)) {
        const newValues = current.filter(v => v !== value);
        searchParams.delete(filter.key);
        newValues.forEach(v => searchParams.append(filter.key, v));
        setOptimisticValue(newValues);
      } else {
        searchParams.append(filter.key, value);
        setOptimisticValue([...current, value]);
      }
    });
  };
  return (
    <div className={(styles.filter__content, styles.sizes__features)}>
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.filter__buttons}>
        {filter?.values.map(val => {
          return (
            <Button
              mode="unset"
              key={val.value}
              className={clsx(
                styles.item,
                styles.size__item,
                searchParams.getAll(filter.key).includes(val.value) &&
                  styles.active,
              )}
              onClick={() => handleValueChange([val.value])}
            >
              <span>{val?.value}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
};

const FilterModalContent = ({
  filters,
}: {
  closeModal: () => void;
  filters: FormattedFilterGroup[];
  productCount?: number;
}) => {
  const [, startTransition] = useTransition();
  const searchParams = useUrlSearchParams(searchParamOptions);
  const colorFilterKey = "sh_swatch_desc";
  const [_optimisticValue, setOptimisticValue] = useOptimistic(
    searchParams.getAll(colorFilterKey),
  );
  const { sizes, colors, features } = useMemo(
    () => ({
      sizes: filters?.find(f => f?.key === "size_name"),
      features: filters?.find(f => f?.key === "pr_features"),
      colors: filters?.find(f => f?.key === "sh_swatch_desc"),
    }),
    [filters],
  );

  const handleValueChange = (values: string[]) => {
    const value = values[0];
    const current = searchParams.getAll(colorFilterKey);

    startTransition(() => {
      if (current.includes(value)) {
        const newValues = current.filter(v => v !== value);
        searchParams.delete(colorFilterKey);
        newValues.forEach(v => searchParams.append(colorFilterKey, v));
        setOptimisticValue(newValues);
      } else {
        searchParams.append(colorFilterKey, value);
        setOptimisticValue([...current, value]);
      }
    });
  };

  return (
    <div>
      <div className={styles.module__filters}>
        <div className={(styles.filter__content, styles.filter__colors)}>
          <h2 className={styles.title}>Colour</h2>
          <div className={styles.filter__buttons}>
            {colors &&
              colors.values.map(color => (
                <Button
                  mode="unset"
                  key={color.value}
                  className={clsx(
                    styles.item,
                    styles.color__blob,
                    styles.colors,
                    searchParams
                      .getAll("sh_swatch_desc")
                      .includes(color.value) && styles.active,
                  )}
                  onClick={() => handleValueChange([color.value])}
                >
                  <span>
                    <span
                      className={styles.color__indicator}
                      style={{
                        background: color?.hex ?? "lightgray",
                      }}
                    />
                    <span>{color?.value}</span>
                  </span>
                </Button>
              ))}
          </div>
        </div>
        {sizes?.values && (
          <FilterBody
            startTransition={startTransition}
            title="Size"
            filter={sizes}
          />
        )}
        {features?.values && (
          <FilterBody
            startTransition={startTransition}
            title="Features"
            filter={features}
          />
        )}
      </div>
    </div>
  );
};

const FilterToolbar = ({}: { filters: FormattedFilterGroup[] }) => {
  const { closeModal } = useModal("filter");
  /*  const hasFilters = !!filters?.tags?.length; */

  return (
    <div className={styles.toolbar}>
      {/* {hasFilters && (
        <Button className={styles.clearButton} onClick={() => null}>
          Clear all
        </Button>
      )} */}

      <Button onClick={closeModal} mode="icon">
        <Close />
      </Button>
    </div>
  );
};

const FilterFooter = ({ productCount }: { productCount?: number }) => {
  const { closeModal } = useModal("filter");
  return (
    <div className={styles.module__footer}>
      <Button color="black" full onClick={closeModal}>
        See all {productCount} results
      </Button>
    </div>
  );
};

const FilterModal = ({
  filters,
  currentAppliedFilters,
  productCount,
}: {
  filters: FormattedFilterGroup[];
  currentAppliedFilters: number;
  productCount?: number;
}) => {
  const { closeModal } = useModal("filter");
  const activeFilterCount = currentAppliedFilters ?? 0;

  const title = activeFilterCount
    ? `Filters (${activeFilterCount})`
    : `Filters`;

  return (
    <Modal
      title={title}
      modal={"filter"}
      always
      className={styles.filters}
      filters={filters}
      toolbar={<FilterToolbar filters={filters} />}
      footer={<FilterFooter productCount={productCount} />}
    >
      <FilterModalContent
        closeModal={closeModal}
        filters={filters}
        productCount={productCount}
      />
    </Modal>
  );
};
export default memo(FilterModal);
